import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const initialState = {
  name: '',
  photo_url: '',
  rating: 5,
  comment: '',
  package_name: '',
};

const TestimonialsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cms/testimonials?show_all=true`);
      setItems(res.data);
    } catch {
      toast.error('Gagal memuat testimoni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleOpen = (item = null) => {
    if (item) {
      setEditing(item);
      setForm({ name: item.name, photo_url: item.photo_url || '', rating: item.rating, comment: item.comment, package_name: item.package_name });
    } else {
      setEditing(null);
      setForm(initialState);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.comment || !form.package_name) {
      toast.error('Nama, komentar, dan paket wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editing) {
        await axios.put(`${API}/cms/testimonials/${editing.id}`, payload);
        toast.success('Testimoni diperbarui');
      } else {
        await axios.post(`${API}/cms/testimonials`, payload);
        toast.success('Testimoni ditambahkan');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await axios.put(`${API}/cms/testimonials/${item.id}`, {
        name: item.name,
        photo_url: item.photo_url || '',
        rating: item.rating,
        comment: item.comment,
        package_name: item.package_name,
        is_active: !item.is_active,
      });
      toast.success(item.is_active ? 'Dinonaktifkan' : 'Diaktifkan');
      fetch();
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus testimoni ini?')) return;
    try {
      await axios.delete(`${API}/cms/testimonials/${id}`);
      toast.success('Testimoni dihapus');
      fetch();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  const renderStars = (n) => {
    const s = [];
    for (let i = 1; i <= 5; i++)
      s.push(<Star key={i} className={`h-4 w-4 ${i <= n ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />);
    return <div className="flex gap-0.5">{s}</div>;
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Memuat data...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold font-['Outfit']">Testimoni Jamaah</h2>
        <Button onClick={() => handleOpen(null)}><Plus className="h-4 w-4 mr-2" />Tambah Testimoni</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <Card key={item.id} className={item.is_active ? '' : 'opacity-50'}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                {item.photo_url ? (
                  <img src={item.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                    {item.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-sm font-semibold truncate">{item.name}</CardTitle>
                  <p className="text-xs text-muted-foreground truncate">{item.package_name}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleToggle(item)} title={item.is_active ? 'Nonaktifkan' : 'Aktifkan'}>
                  {item.is_active ? <ToggleRight className="h-4 w-4 text-green-500" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-2">{renderStars(item.rating)}</div>
              <p className="text-sm text-muted-foreground line-clamp-3">{item.comment}</p>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => handleOpen(item)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="h-3 w-3 mr-1" />Hapus</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">Belum ada testimoni</p>}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Testimoni' : 'Tambah Testimoni'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Jamaah</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" />
            </div>
            <div>
              <Label>Foto URL (opsional)</Label>
              <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." />
              {form.photo_url && <img src={form.photo_url} alt="Preview" className="w-12 h-12 rounded-full object-cover mt-2" onError={(e) => e.target.style.display='none'} />}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating (1-5)</Label>
                <Input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
              </div>
              <div>
                <Label>Nama Paket</Label>
                <Input value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} placeholder="Paket Umrah Premium" />
              </div>
            </div>
            <div>
              <Label>Komentar</Label>
              <textarea
                className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Testimoni jamaah..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsManagement;
